import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { ActorChip } from './PipelineStrip';
import {
  handoffTrendX,
  type HandoffConditionRead,
  type HandoffStopRead,
  type HandoffTrendChart,
} from './HandoffRegion.transform';

// W3d: the bearing case page's handoff region (equipment-beat comp, Handoff A).
// Three columns on the zinc case's grid: the condition evidence, the one-off
// stop pair and the Maintenance handoff (`StopAction`, passed in as a slot so
// its gate keeps W3c's behaviour). The routing graph rides under the evidence.

const CHART_H = 204;

/**
 * The comp's card (`.card h4`, DESIGN.md § Type ramp: the panel title is quieter
 * than the figures under it, 13px 600): `card-matte-2` face, title and its
 * meta or actor chip on one row, hairline under the header, padded body.
 */
export function HandoffPanel({ title, meta, actions, children, className }: {
  title: string;
  meta?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('card-matte-2 flex h-full min-w-0 flex-col', className)}>
      <div className="hairline-b flex items-center gap-2 px-4 py-2">
        <h2 className="min-w-0 flex-1 text-[13px] font-semibold leading-snug text-ink-primary">{title}</h2>
        {meta ? <span className="shrink-0 text-xs tabular-nums text-muted" data-handoff-panel-meta>{meta}</span> : null}
        {actions ? <span className="shrink-0">{actions}</span> : null}
      </div>
      <div className="min-h-0 flex-1 px-4 py-3">{children}</div>
    </div>
  );
}
const PAD = { l: 26, r: 8, t: 8, b: 20 } as const;
const TICK_PX = 10;
/** Half an `08 Jun` label at the tick size. */
const TICK_HALF_LABEL = 17;

/** The week's TE-622-625 trace: trip gap, Band high and spec high, the above-band run tinted from its crossing. */
function BearingTrendChart({ chart }: { chart: HandoffTrendChart }) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(334);
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const innerW = width - PAD.l - PAD.r;
  const innerH = CHART_H - PAD.t - PAD.b;
  const x = (at: string) => PAD.l + innerW * handoffTrendX(at as HandoffTrendChart['window']['startAt'], chart.window);
  const y = (value: number) => PAD.t + innerH * ((chart.yDomain.max - value) / (chart.yDomain.max - chart.yDomain.min));

  let line = '';
  let penDown = false;
  for (const point of chart.points) {
    if (point.value === null) { penDown = false; continue; }
    line += `${penDown ? 'L' : 'M'}${x(point.at).toFixed(1)} ${y(point.value).toFixed(1)}`;
    penDown = true;
  }
  const above = chart.aboveBand;
  const aboveArea = above.length > 1
    ? `M${x(above[0]!.at).toFixed(1)} ${y(chart.bandHigh).toFixed(1)}${above
        .map((point) => `L${x(point.at).toFixed(1)} ${y(Math.max(chart.bandHigh, point.value)).toFixed(1)}`)
        .join('')}L${x(above.at(-1)!.at).toFixed(1)} ${y(chart.bandHigh).toFixed(1)}Z`
    : null;

  return (
    <div ref={wrapperRef} data-handoff-trend>
      <svg
        width={width}
        height={CHART_H}
        viewBox={`0 0 ${width} ${CHART_H}`}
        className="block"
        role="img"
        aria-label={`Bearing temperature over the week against Band high ${chart.bandHigh} ${chart.unit} and spec high ${chart.specHigh} ${chart.unit}`}
      >
        {chart.yTicks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(tick)} y2={y(tick)} stroke="var(--hairline-strong)" />
            <text x={PAD.l - 5} y={y(tick) + 3.5} fontSize={TICK_PX} textAnchor="end" fill="var(--ink-tertiary)">{tick}</text>
          </g>
        ))}
        {chart.dayTicks.map((tick) => {
          // A tick near the right edge keeps its label, end-anchored at the plot edge.
          const tx = x(tick.at);
          const atEdge = tx + TICK_HALF_LABEL > width - PAD.r;
          return (
            <text key={tick.at} data-handoff-day-tick={tick.label} x={atEdge ? width - PAD.r : tx} y={CHART_H - 5} fontSize={TICK_PX} textAnchor={atEdge ? 'end' : 'middle'} fill="var(--ink-tertiary)">
              {tick.label}
            </text>
          );
        })}
        {chart.gaps.map((gap) => (
          <rect key={gap.startAt} data-handoff-gap x={x(gap.startAt)} y={PAD.t} width={Math.max(2, x(gap.endAt) - x(gap.startAt))} height={innerH} fill="var(--ink-tertiary)" opacity={0.14} />
        ))}
        {aboveArea ? <path d={aboveArea} fill="var(--state-blocked)" opacity={0.16} data-handoff-above-band={chart.crossingAt ?? undefined} /> : null}
        <line data-band-high={chart.bandHigh} x1={PAD.l} x2={width - PAD.r} y1={y(chart.bandHigh)} y2={y(chart.bandHigh)} stroke="var(--state-warning)" strokeWidth={1.2} strokeDasharray="4 3" />
        <line data-spec-high={chart.specHigh} x1={PAD.l} x2={width - PAD.r} y1={y(chart.specHigh)} y2={y(chart.specHigh)} stroke="var(--state-blocked)" strokeWidth={1.2} strokeDasharray="4 3" />
        <path d={line} fill="none" stroke="var(--ink-primary)" strokeWidth={1.3} strokeLinejoin="round" />
        {chart.latest ? (
          <circle cx={x(chart.latest.at)} cy={y(chart.latest.value)} r={3.2} fill="var(--state-blocked)" stroke="var(--surface-2)" strokeWidth={1.2} />
        ) : null}
      </svg>
    </div>
  );
}

/** Label left, value right, hairline-divided: the comp's `.evi` list. */
function FactList({ rows }: { rows: readonly { id?: string; label: string; value: string; tone?: 'blocked' }[] }) {
  return (
    <dl className="divide-y divide-[var(--color-divider)] text-[12px] leading-snug tabular-nums">
      {rows.map((row) => (
        <div
          key={row.id ?? row.label}
          className="flex items-baseline justify-between gap-3 py-1.5 first:pt-0 last:pb-0"
          data-case-fact={row.id}
          data-control-plan-limit={row.id === 'Spec high horizon' ? 'high' : undefined}
        >
          <dt className="min-w-0 text-ink-secondary">{row.label}</dt>
          <dd className={cn('shrink-0 text-right font-semibold', row.tone === 'blocked' ? 'text-[var(--state-blocked)]' : 'text-ink-primary')}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StopPair({ read }: { read: HandoffStopRead }) {
  return (
    <div className="flex flex-col gap-3 tabular-nums" data-handoff-stop-pair>
      {read.bars.map((bar) => (
        <div key={bar.id} data-stop-bar={bar.id}>
          <div className="flex items-baseline justify-between gap-2.5">
            <span className="min-w-0 text-[12.5px] text-ink-primary">
              {bar.label} <span className="text-muted">· {bar.detail}</span>
            </span>
            <span
              className={cn('shrink-0 text-[17px] font-semibold leading-tight', bar.id === 'avoided' ? 'text-[var(--state-blocked)]' : 'text-ink-primary')}
              data-stop-amount={bar.id}
            >
              {bar.amount}
            </span>
          </div>
          <div className="relative mt-1.5 h-3 overflow-hidden rounded bg-[var(--hairline-strong)]" aria-hidden="true">
            <i
              className={cn(
                'absolute inset-y-0 left-0 block rounded',
                bar.id === 'planned'
                  ? 'bg-ink-secondary'
                  : 'bg-[repeating-linear-gradient(135deg,var(--state-blocked)_0_5px,color-mix(in_srgb,var(--state-blocked)_70%,transparent)_5px_8px)]',
              )}
              style={{ width: `${bar.widthPct.toFixed(1)}%` }}
            />
          </div>
        </div>
      ))}
      <p className="border-t border-[var(--color-divider)] pt-2 text-[11.5px] leading-snug text-muted" data-stop-basis>
        {read.basis.rate ? <>hours × <b className="font-semibold text-ink-secondary">{read.basis.rate}</b> </> : null}{read.basis.text}
      </p>
      <FactList rows={read.rows} />
    </div>
  );
}

export function HandoffRegion({
  condition,
  stop,
  routing,
  handoff,
}: {
  condition: HandoffConditionRead;
  stop: HandoffStopRead | null;
  routing: ReactNode;
  handoff: ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-3" data-handoff-region>
      <Commentable name="AdvisoryCasePage.Evidence">
        <section className="h-full min-w-0" data-case-section="evidence">
          <HandoffPanel title={condition.title} meta={condition.period}>
            <div className="space-y-3">
              <BearingTrendChart chart={condition.chart} />
              <FactList rows={condition.facts} />
              {routing}
            </div>
          </HandoffPanel>
        </section>
      </Commentable>
      {stop ? (
        <Commentable name="AdvisoryCasePage.StopPair">
        <section className="h-full min-w-0" data-case-section="stop-pair">
          <HandoffPanel title="One-off stop · planned vs avoided" actions={<ActorChip actor="Engine" />}>
            <StopPair read={stop} />
          </HandoffPanel>
        </section>
        </Commentable>
      ) : null}
      {handoff}
    </div>
  );
}
